import {RestService} from "@/services/rest/RestService";
import {CacheService} from "@/services/cache/CacheService";
import {CacheKeyEnum} from "@/services/cache/CacheKeyEnum";
import {ProductBrandAPIApi, ProductCategoryAPIApi, ProductTrademarkAPIApi} from "@/api/pharma-cpc-product";
import {
    CodeDescriptionRestDto,
    MasterProductCategoryRestDto,
    ProductCategoryOverviewRestDto,
    ProductCategoryRestDto
} from "@/api/pharma-cpc-product/models";
import {CustomerContext} from "@/context/CustomerContext";
import {AxiosResponse} from "axios";

export class ProductRestService extends RestService {

    private trademarkApiService: ProductTrademarkAPIApi;
    private brandApiService: ProductBrandAPIApi;
    private categoryApiService: ProductCategoryAPIApi;

    protected constructor() {
        super();

        this.trademarkApiService = new ProductTrademarkAPIApi(this.getDpAxiosInstance(), undefined, this.getConfig().cpcProductUrl);
        this.brandApiService = new ProductBrandAPIApi(this.getDpAxiosInstance(), undefined, this.getConfig().cpcProductUrl);
        this.categoryApiService = new ProductCategoryAPIApi(this.getDpAxiosInstance(), undefined, this.getConfig().cpcProductUrl);
    }

    public async getAllTrademarks(customerCode: string): Promise<Array<CodeDescriptionRestDto>> {
        const nonNullCustomerCode = customerCode ?? CustomerContext.getCustomerCode();
        let promise: Promise<AxiosResponse<CodeDescriptionRestDto[]>> =
            CacheService.getInstance().get(CacheKeyEnum.PRODUCT_TRADEMARKS, nonNullCustomerCode);

        if (promise !== null) {
            const result: AxiosResponse<CodeDescriptionRestDto[]> = await promise;
            return result.data;
        }

        promise = this.trademarkApiService.findProductTrademarks(nonNullCustomerCode);

        CacheService.getInstance().set(CacheKeyEnum.PRODUCT_TRADEMARKS, promise, nonNullCustomerCode);

        const result: AxiosResponse<CodeDescriptionRestDto[]> = await promise;
        return result.data;
    }

    public async getAllBrands(customerCode: string): Promise<Array<CodeDescriptionRestDto>> {
        const nonNullCustomerCode = customerCode ?? CustomerContext.getCustomerCode();
        let promise: Promise<AxiosResponse<CodeDescriptionRestDto[]>> =
            CacheService.getInstance().get(CacheKeyEnum.PRODUCT_BRANDS, nonNullCustomerCode);

        if (promise !== null) {
            const result: AxiosResponse<CodeDescriptionRestDto[]> = await promise;
            return result.data;
        }

        promise = this.brandApiService.findProductBrands(nonNullCustomerCode);
        CacheService.getInstance().set(CacheKeyEnum.PRODUCT_BRANDS, promise, nonNullCustomerCode);

        const result: AxiosResponse<CodeDescriptionRestDto[]> = await promise;
        return result.data;
    }

    public async getAllCategoriesCustomer(customerCode: string): Promise<Array<ProductCategoryOverviewRestDto>> {
        const nonNullCustomerCode = customerCode ?? CustomerContext.getCustomerCode();
        let promise: Promise<AxiosResponse<ProductCategoryOverviewRestDto[]>> =
            CacheService.getInstance().get(CacheKeyEnum.PRODUCT_CATEGORIES_ALL, nonNullCustomerCode);

        if (promise !== null) {
            const result: AxiosResponse<ProductCategoryOverviewRestDto[]> = await promise;
            return result.data;
        }

        promise = this.categoryApiService.findProductCategories(nonNullCustomerCode);
        CacheService.getInstance().set(CacheKeyEnum.PRODUCT_CATEGORIES_ALL, promise, nonNullCustomerCode);

        const result: AxiosResponse<ProductCategoryOverviewRestDto[]> = await promise;
        return result.data;
    }

    public async getProductCategoriesTreeCustomer(customerCode: string): Promise<ProductCategoryRestDto> {
        const nonNullCustomerCode = customerCode ?? CustomerContext.getCustomerCode();
        let promise: Promise<AxiosResponse<ProductCategoryRestDto>> =
            CacheService.getInstance().get(CacheKeyEnum.PRODUCT_CATEGORIES_TREE, nonNullCustomerCode);

        if (promise !== null) {
            const result: AxiosResponse<ProductCategoryRestDto> = await promise;
            return result.data;
        }

        promise = this.categoryApiService.findProductCategoriesTree(nonNullCustomerCode);
        CacheService.getInstance().set(CacheKeyEnum.PRODUCT_CATEGORIES_TREE, promise, nonNullCustomerCode);

        const result: AxiosResponse<ProductCategoryRestDto> = await promise;
        return result.data;
    }

    public async getProductCategoriesMasterTree(): Promise<MasterProductCategoryRestDto> {
        const nonNullCustomerCode = CustomerContext.getCustomerCode();
        let promise: Promise<AxiosResponse<MasterProductCategoryRestDto>> =
            CacheService.getInstance().get(CacheKeyEnum.PRODUCT_CATEGORIES_MASTER_TREE, nonNullCustomerCode);

        if (promise !== null) {
            const result: AxiosResponse<MasterProductCategoryRestDto> = await promise;
            return result.data;
        }

        promise = this.categoryApiService.findProductCategoriesMasterTree();
        CacheService.getInstance().set(CacheKeyEnum.PRODUCT_CATEGORIES_MASTER_TREE, promise, nonNullCustomerCode);

        const result: AxiosResponse<MasterProductCategoryRestDto> = await promise;
        return result.data;
    }
}
